import { apiFetch, type IdResponse, type OperationResult } from "@/lib/apiClient";
import { fetchAll, pageQuery, toPaged, type Paged, type PageParams } from "@/lib/paging";
import { userRef, toUserRef, type UserRef } from "@/utils/userRef";

export type { UserRef };

interface MerchantListResponse {
  id: string;
  mid: string;
  dbaName: string;
  legalName: string | null;
  status: string;
  userId: string;
  associateUserId: string | null;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  netProfit: number | string;
  creationTime: string;
  ownerName: string | null;
  associateName: string | null;
  statementCount: number | string;
  lastProcessor: string | null;
  lastVolume: number | string | null;
  avgVolume: number | string | null;
  approvedDate: string | null;
  closedDate: string | null;
  owner?: unknown;
  associate?: unknown;
}

interface MerchantStatementResponse {
  id: string;
  periodMonth: string;
  netVolume: number | string;
  netProfit: number | string;
  processor: string;
  totalTickets?: number | string;
  userPayout?: number | string;
  chargebackCount?: number | string;
}

interface MerchantDetailResponse extends MerchantListResponse {
  mcc: string | null;
  boardingDataJson: string | null;
  statementCount: number | string;
  recentStatements: MerchantStatementResponse[] | null;
  lastProcessor: string | null;
  lastVolume: number | string | null;
  avgVolume: number | string | null;
  assistants: unknown[] | null;
  riskTier: number | string | null;
  approvedDate: string | null;
  closedDate: string | null;
  lastDepositDate: string | null;
  contactName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  zip: string | null;
  taxId: string | null;
  ein: string | null;
  ssn: string | null;
  bankAccountNumber: string | null;
  bankRoutingNumber: string | null;
  pctCardSwiped: number | string | null;
  pctKeyedCardPresent: number | string | null;
  pctKeyedCardNotPresent: number | string | null;
  pctEcommerce: number | string | null;
  pctB2B: number | string | null;
  avgTicketAmount: number | string | null;
  monthlyVolumeEstimate: number | string | null;
  maxTransactionAmount: number | string | null;
  assocNumber: string | null;
  assocName: string | null;
  prospectNumber: string | null;
  thisMonthRatio: number | string | null;
  commissionRateOverride: number | string | null;
  notes: string | null;
  lastModificationTime: string | null;
}

function toMerchantListItem(r: MerchantListResponse): MerchantListItem {
  return {
    id: r.id,
    mid: r.mid,
    dbaName: r.dbaName,
    legalName: r.legalName ?? null,
    status: r.status,
    phone: r.phone ?? null,
    addressLine1: r.addressLine1 ?? null,
    city: r.city ?? null,
    state: r.state ?? null,
    zip: r.zip ?? null,
    owner: r.owner ? toUserRef(r.owner) : (userRef(r.userId, r.ownerName) ?? { id: r.userId, name: r.ownerName ?? "" }),
    associate: r.associate ? toUserRef(r.associate) : userRef(r.associateUserId, r.associateName),
    approvedDate: r.approvedDate ?? null,
    lastProcessor: r.lastProcessor ?? null,
    lastVolume: numOrNull(r.lastVolume),
    avgVolume: numOrZero(r.avgVolume),
    statementCount: numOrZero(r.statementCount),
    closedDate: r.closedDate ?? null,
  };
}

function numOrNull(v: number | string | null | undefined): number | null {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function numOrZero(v: number | string | null | undefined): number {
  return numOrNull(v) ?? 0;
}

function toMerchantDetail(r: MerchantDetailResponse): MerchantDetail {
  return {
    id: r.id,
    mid: r.mid,
    dbaName: r.dbaName,
    legalName: r.legalName ?? null,
    mcc: r.mcc ?? null,
    status: r.status,
    approvedDate: r.approvedDate ?? null,
    closedDate: r.closedDate ?? null,
    lastDepositDate: r.lastDepositDate ?? null,
    phone: r.phone ?? null,
    email: r.email ?? null,
    contactName: r.contactName ?? null,
    addressLine1: r.addressLine1 ?? null,
    addressLine2: r.addressLine2 ?? null,
    city: r.city ?? null,
    state: r.state ?? null,
    zip: r.zip ?? null,
    taxId: r.taxId ?? null,
    ein: r.ein ?? null,
    ssn: r.ssn ?? null,
    bankAccountNumber: r.bankAccountNumber ?? null,
    bankRoutingNumber: r.bankRoutingNumber ?? null,
    assocNumber: r.assocNumber ?? null,
    assocName: r.assocName ?? null,
    prospectNumber: r.prospectNumber ?? null,
    commissionRateOverride: numOrNull(r.commissionRateOverride),
    notes: r.notes ?? null,
    netProfit: numOrZero(r.netProfit),
    thisMonthRatio: numOrNull(r.thisMonthRatio),
    createdAt: r.creationTime,
    updatedAt: r.lastModificationTime ?? r.creationTime,
    owner: r.owner ? toUserRef(r.owner) : (userRef(r.userId, r.ownerName) ?? { id: r.userId, name: r.ownerName ?? "" }),
    associate: r.associate ? toUserRef(r.associate) : userRef(r.associateUserId, r.associateName),
    assistants: (r.assistants ?? []).map(toUserRef),
    statementCount: numOrZero(r.statementCount),
    recentStatements: (r.recentStatements ?? []).map((s) => ({
      id: s.id,
      periodMonth: s.periodMonth,
      processor: s.processor,
      netVolume: numOrZero(s.netVolume),
      totalTickets: numOrZero(s.totalTickets),
      netProfit: numOrZero(s.netProfit),
      userPayout: numOrZero(s.userPayout),
      chargebackCount: numOrZero(s.chargebackCount),
    })),
    boardingDataJson: r.boardingDataJson ?? null,
    pctCardSwiped: numOrNull(r.pctCardSwiped),
    pctKeyedCardPresent: numOrNull(r.pctKeyedCardPresent),
    pctKeyedCardNotPresent: numOrNull(r.pctKeyedCardNotPresent),
    pctEcommerce: numOrNull(r.pctEcommerce),
    pctB2B: numOrNull(r.pctB2B),
    avgTicketAmount: numOrNull(r.avgTicketAmount),
    monthlyVolumeEstimate: numOrNull(r.monthlyVolumeEstimate),
    maxTransactionAmount: numOrNull(r.maxTransactionAmount),
  };
}

export interface MerchantListItem {
  id: string;
  mid: string;
  dbaName: string;
  legalName: string | null;
  status: string;
  phone: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  owner: UserRef;
  associate: UserRef | null;
  approvedDate: string | null;
  lastProcessor: string | null;
  lastVolume: number | null;
  avgVolume: number;
  statementCount: number;
  closedDate: string | null;
}

export interface MerchantStatementSummary {
  id: string;
  periodMonth: string;
  processor: string;
  netVolume: number;
  totalTickets: number;
  netProfit: number;
  userPayout: number;
  chargebackCount: number;
}

export interface MerchantDetail {
  id: string;
  mid: string;
  dbaName: string;
  legalName: string | null;
  mcc: string | null;
  status: string;
  approvedDate: string | null;
  closedDate: string | null;
  lastDepositDate: string | null;
  phone: string | null;
  email: string | null;
  contactName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  taxId: string | null;
  ein: string | null;
  ssn: string | null;
  bankAccountNumber: string | null;
  bankRoutingNumber: string | null;
  assocNumber: string | null;
  assocName: string | null;
  prospectNumber: string | null;
  commissionRateOverride: number | null;
  notes: string | null;
  netProfit: number;
  thisMonthRatio: number | null;
  createdAt: string;
  updatedAt: string;
  owner: UserRef;
  associate: UserRef | null;
  assistants: UserRef[];
  statementCount: number;
  recentStatements: MerchantStatementSummary[];

  boardingDataJson: string | null;

  pctCardSwiped: number | null;
  pctKeyedCardPresent: number | null;
  pctKeyedCardNotPresent: number | null;
  pctEcommerce: number | null;
  pctB2B: number | null;
  avgTicketAmount: number | null;
  monthlyVolumeEstimate: number | null;
  maxTransactionAmount: number | null;
}

export interface MerchantSummary {
  total: number;
  live: number;
  approved: number;
  inactive: number;
  closed: number;
}

const MERCHANT_SEARCH_COLS = ["mid", "dbaName", "legalName", "city", "contactName"];

export const merchants = {
  summary(params: PageParams = {}) {
    return apiFetch<MerchantSummary>(
      `/merchants/summary${pageQuery(params, undefined, MERCHANT_SEARCH_COLS)}`,
    );
  },
  async list(params: PageParams = {}): Promise<Paged<MerchantListItem>> {
    const res = await apiFetch<Paged<MerchantListResponse> | MerchantListResponse[]>(
      `/merchants${pageQuery(params, undefined, MERCHANT_SEARCH_COLS)}`,
    );
    const p = toPaged<MerchantListResponse>(res);
    return { ...p, items: p.items.map(toMerchantListItem) };
  },
  listAll(params: PageParams = {}): Promise<MerchantListItem[]> {
    return fetchAll((p) => this.list(p), params, "merchants");
  },
  async get(id: string) {
    const r = await apiFetch<MerchantDetailResponse>(`/merchants/${encodeURIComponent(id)}`);
    return toMerchantDetail(r);
  },

  saveBoarding(id: string, data: Record<string, unknown>) {
    return apiFetch<OperationResult>(
      `/merchants/${encodeURIComponent(id)}/boarding`,
      { method: "PUT", body: JSON.stringify({ boardingDataJson: JSON.stringify(data) }) },
    );
  },

  setMid(id: string, mid: string) {
    return apiFetch<OperationResult>(
      `/merchants/${encodeURIComponent(id)}/mid`,
      { method: "PUT", body: JSON.stringify({ mid }) },
    );
  },

  setOwner(id: string, userId: string) {
    return apiFetch<OperationResult>(
      `/merchants/${encodeURIComponent(id)}/owner`,
      { method: "PUT", body: JSON.stringify({ userId }) },
    );
  },

  setAssociate(id: string, associateUserId: string | null) {
    return apiFetch<OperationResult>(
      `/merchants/${encodeURIComponent(id)}/associate`,
      { method: "PUT", body: JSON.stringify({ associateUserId }) },
    );
  },
};

export interface MerchantTerminalRow {
  id: string;
  model: string;
  serialNumber: string | null;
  status: string;
  notes: string | null;
  assignedAt: string | null;
  createdAt: string;
  updatedAt: string;
  productId: string | null;
  productName: string | null;
}

export interface SaveMerchantTerminalRequest {
  model: string;
  serialNumber?: string | null;
  status?: string;
  notes?: string | null;
  assignedAt?: string | null;
  productId?: string | null;
}

export interface MerchantPrincipalRow {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  ownershipPercentage: number | null;
  email: string | null;
  phone: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveMerchantPrincipalRequest {
  firstName: string;
  lastName: string;
  title?: string | null;
  ownershipPercentage?: number | null;
  email?: string | null;
  phone?: string | null;
}

export interface MerchantFeeScheduleRow {
  id: string;
  effectiveAt: string;
  notes: string | null;
  visaMcDiscoverRate: number | null;
  americanExpressOptBlueRate: number | null;
  transactionFee: number | null;
  authorizationFee: number | null;
  monthlyFee: number | null;
  monthlyPciFee: number | null;
  monthlyMinimumFee: number | null;
  batchFee: number | null;
  chargebackFee: number | null;
  annualFee: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveMerchantFeeScheduleRequest {
  effectiveAt?: string;
  notes?: string | null;
  visaMcDiscoverRate?: number | null;
  americanExpressOptBlueRate?: number | null;
  transactionFee?: number | null;
  authorizationFee?: number | null;
  monthlyFee?: number | null;
  monthlyPciFee?: number | null;
  monthlyMinimumFee?: number | null;
  batchFee?: number | null;
  chargebackFee?: number | null;
  annualFee?: number | null;
}

export interface MerchantUserRow {
  id: string;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  merchantRole: string;
  createdAt: string;
  updatedAt: string;
}

export interface SaveMerchantUserRequest {
  name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  merchantRole?: string;
}

export interface MerchantStoreRow {
  id: string;
  storeId: string;
  parentStoreId: string | null;
  giftCardStoreId: string | null;
  posDbaName: string | null;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SaveMerchantStoreRequest {
  storeId: string;
  parentStoreId?: string | null;
  giftCardStoreId?: string | null;
  posDbaName?: string | null;
  note?: string | null;
}

function withTimestamps(r: Record<string, unknown>): Record<string, unknown> {
  const ct = (r.creationTime as string) ?? "";
  return { ...r, createdAt: ct, updatedAt: (r.lastModificationTime as string) ?? ct };
}

export const merchantSubresources = {
  terminals: makeCrud<MerchantTerminalRow, SaveMerchantTerminalRequest>(
    "terminals",
    (r) => ({ ...withTimestamps(r), productName: (r.productName as string) ?? null }) as unknown as MerchantTerminalRow,
  ),
  principals: makeCrud<MerchantPrincipalRow, SaveMerchantPrincipalRequest>(
    "principals",
    (r) => withTimestamps(r) as unknown as MerchantPrincipalRow,
  ),
  feeSchedule: makeCrud<MerchantFeeScheduleRow, SaveMerchantFeeScheduleRequest>(
    "fee-schedule",
    (r) => withTimestamps(r) as unknown as MerchantFeeScheduleRow,
  ),
  users: makeCrud<MerchantUserRow, SaveMerchantUserRequest>(
    "users",
    (r) => withTimestamps(r) as unknown as MerchantUserRow,
  ),
  stores: makeCrud<MerchantStoreRow, SaveMerchantStoreRequest>(
    "stores",
    (r) => withTimestamps(r) as unknown as MerchantStoreRow,
  ),
};

function makeCrud<TRow, TReq>(
  resource: string,
  adapt: (raw: Record<string, unknown>) => TRow,
) {
  return {
    async list(merchantId: string): Promise<TRow[]> {
      const rows = await apiFetch<Paged<Record<string, unknown>> | Record<string, unknown>[]>(
        `/merchants/${encodeURIComponent(merchantId)}/${resource}`,
      );
      return toPaged<Record<string, unknown>>(rows).items.map(adapt);
    },
    create(merchantId: string, body: TReq) {
      return apiFetch<IdResponse>(`/merchants/${encodeURIComponent(merchantId)}/${resource}`, {
        method: "POST",
        body: JSON.stringify(body),
      });
    },
    update(merchantId: string, id: string, body: TReq) {
      return apiFetch<OperationResult>(
        `/merchants/${encodeURIComponent(merchantId)}/${resource}/${encodeURIComponent(id)}`,
        { method: "PUT", body: JSON.stringify(body) },
      );
    },
    remove(merchantId: string, id: string) {
      return apiFetch<void>(
        `/merchants/${encodeURIComponent(merchantId)}/${resource}/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      );
    },
  };
}
